import UploadIcon from "../../assets/icons/UploadIcon";
import CameraIcon from "../../assets/icons/CameraIcon";
// import TrashIcon from "../../assets/icons/TrashIcon"; // Import a trash icon for delete functionality
import { useMemo, useState } from "react";
import useAcceptedFileTypes from "../../utils/hooks/useAcceptedFileTypes";
import prettyBytes from "pretty-bytes";

export type FileInputParams = {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
};

const FileDropZone = ({
  name = "dropzone",
  value = [],
  accept,
  multiple = true,
  maxSize,
  error,
  className,
  setError = () => { },
  onChange = () => { },
  onBlur,
}: FileInputParams & {
  name?: string;
  value?: File[];
  error?: boolean;
  className?: string;
  onChange?: (value: File[]) => void;
  setError?: (message: string) => void;
  onBlur?: () => void;
}) => {
  const fileAccepted = useAcceptedFileTypes(accept);
  const [dragTarget, setDragTarget] = useState<EventTarget | undefined>();
  const [fileList, setFileList] = useState<File[]>(value);

  const dropZoneClassName = useMemo(
    () =>
      dragTarget !== undefined
        ? "border-blue-400"
        : error
          ? "border-red-500"
          : "border-gray-200",
    [dragTarget, error]
  );

  const handleChange = (files: FileList) => {
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i) as File;

      // Ignore duplicates
      if (multiple && fileList.find((f) => f.name === file.name)) continue;

      // Validate file type
      if (!fileAccepted(file)) {
        const filename = file.name.split(".");
        setError(`Unsupported file type .${filename[filename.length - 1]}`);
        return;
      }

      // Validate file size
      if (maxSize && file.size > maxSize) {
        setError(`File too large, maximum size of ${prettyBytes(maxSize)}`);
        return;
      }

      newFiles.push(file);
    }

    const updatedFiles = multiple ? [...fileList, ...newFiles] : newFiles;
    setFileList(updatedFiles);
    onChange(updatedFiles);
  };

  const handleDelete = (fileName: string) => {
    const updatedFiles = fileList.filter((file) => file.name !== fileName);
    setFileList(updatedFiles);
    onChange(updatedFiles);
  };

  return (
    <section className={`relative w-full p-4 ${className}`} onBlur={onBlur}>
      {/* Drag/drop zone */}
      <div
        className={`relative z-10 flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-white p-6 ${dropZoneClassName}`}
        onDrop={(e) => {
          e.preventDefault();
          handleChange(e.dataTransfer.files);
          setDragTarget(undefined);
        }}
        onDragEnter={(e) => {
          setDragTarget(e.target);
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          if (dragTarget === e.target) setDragTarget(undefined);
          e.stopPropagation();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
      >
        <UploadIcon className="stroke-blue-400 mb-3" />
        <p className="text-gray-600 mb-2">Drag and drop</p>
        <p className="text-sm text-gray-400">or</p>
        <input
          id={name}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleChange(e.target.files || new FileList())}
        />
        <label
          htmlFor={name}
          className="mt-2 cursor-pointer rounded-lg bg-blue-500 px-4 py-2 text-white shadow transition hover:bg-blue-600"
        >
          Upload
        </label>
        <p className="mt-2 text-xs text-gray-400">
          JPG or PNG only. {maxSize && `Max size: ${prettyBytes(maxSize)}`}
        </p>
      </div>

      {/* File list */}
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">File List</h3>
        <ul className="space-y-2">
          {fileList.map((file) => (
            <li
              key={file.name}
              className="flex justify-between items-center rounded-md bg-gray-50 p-2 shadow-sm"
            >
              <div>
                <p className="text-sm font-medium text-gray-800">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {prettyBytes(file.size)}
                </p>
              </div>
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => handleDelete(file.name)}
              >

              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default FileDropZone;
